/**
 * GlobalOverlay — persistent on-screen elements that float above every scene.
 *
 * All overlay elements now respect overlayGeometry from SceneProps so the
 * Layout Library editor can reposition/resize them visually.
 *
 * Layers (bottom → top):
 *   1. Top bar         (channel name / show title strip)
 *   2. Logo            (top-right corner, always visible)
 *   3. Story counter   (top-left badge, appears at scene start)
 *   4. Breaking banner (full-width red strip, replaces top bar when active)
 *   5. Ticker          (bottom crawl)
 *
 * Usage: place <GlobalOverlay props={sceneProps} /> as the LAST child of any
 *         scene's AbsoluteFill so it renders on top of all scene content.
 */
import React from 'react';
import { AbsoluteFill, Img, interpolate, useCurrentFrame, useVideoConfig } from 'remotion';
import type { SceneProps, OverlayElementGeometry, OverlayColorOverride } from '../types';
import { DEFAULT_OVERLAY_GEOMETRY } from '../types';

// ─── Top Bar ──────────────────────────────────────────────────────────────────

const TopBar: React.FC<{
  text?: string;
  bgColor: string;
  accentColor: string;
  geo: OverlayElementGeometry;
  oc?: OverlayColorOverride;
}> = ({ text = 'LIVE NEWS', bgColor, accentColor, geo, oc }) => {
  const frame = useCurrentFrame();
  const y = interpolate(frame, [0, 12], [-geo.h, 0], { extrapolateRight: 'clamp' });
  const resolvedBg = oc?.bgColor ?? bgColor;
  const resolvedAccent = oc?.accentColor ?? accentColor;
  const resolvedText = oc?.textColor ?? '#fff';

  return (
    <div style={{
      position: 'absolute',
      top: geo.y, left: geo.x,
      width: geo.w, height: geo.h,
      zIndex: geo.zIndex ?? 20,
      background: oc?.bgImageUrl ? 'transparent' : resolvedBg,
      borderBottom: `3px solid ${resolvedAccent}`,
      display: 'flex', alignItems: 'center',
      padding: '0 24px', gap: 12,
      transform: `translateY(${y}px)`,
      overflow: 'hidden',
      boxSizing: 'border-box',
    }}>
      {oc?.bgImageUrl && (
        <img src={oc.bgImageUrl} alt="" style={{ position: 'absolute', inset: 0, width: '100%', height: '100%', objectFit: 'cover' }} />
      )}
      {!oc?.bgImageUrl && <div style={{ position: 'absolute', inset: 0, background: resolvedBg }} />}
      <div style={{ position: 'relative', zIndex: 1, display: 'flex', alignItems: 'center', gap: 12, width: '100%' }}>
        <LiveDot />
        <div style={{
          fontFamily: 'Roboto, sans-serif',
          fontSize: geo.fontSize, fontWeight: 800,
          color: resolvedText, letterSpacing: 3,
          textTransform: 'uppercase',
          whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis',
        }}>
          {text}
        </div>
      </div>
    </div>
  );
};

// ─── Live Dot ──────────────────────────────────────────────────────────────────

const LiveDot: React.FC = () => {
  const frame = useCurrentFrame();
  const pulse = Math.sin(frame * 0.18) * 0.4 + 0.6;
  return (
    <div style={{ position: 'relative', width: 10, height: 10, flexShrink: 0 }}>
      <div style={{
        position: 'absolute', inset: -3,
        borderRadius: '50%', border: '2px solid #ff3333',
        opacity: pulse * 0.6,
      }} />
      <div style={{
        width: 10, height: 10, borderRadius: '50%',
        background: '#ff3333', opacity: pulse,
      }} />
    </div>
  );
};

// ─── Logo ──────────────────────────────────────────────────────────────────────

const LogoBadge: React.FC<{
  logoAsset?: SceneProps['assets']['logo'];
  accentColor: string;
  geo: OverlayElementGeometry;
}> = ({ logoAsset, accentColor, geo }) => {
  const frame = useCurrentFrame();
  const opacity = interpolate(frame, [0, 18], [0, 1], { extrapolateRight: 'clamp' });

  if (!logoAsset) {
    return (
      <div style={{
        position: 'absolute',
        top: geo.y, left: geo.x,
        width: geo.w, height: geo.h,
        zIndex: geo.zIndex ?? 30,
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        background: accentColor, padding: '6px 14px',
        borderRadius: 4, opacity, boxSizing: 'border-box',
      }}>
        <div style={{
          fontFamily: 'Roboto, sans-serif', fontSize: geo.fontSize,
          fontWeight: 900, color: '#000', letterSpacing: 2,
          textTransform: 'uppercase',
        }}>
          BUZZ24
        </div>
      </div>
    );
  }

  return (
    <div style={{
      position: 'absolute',
      top: geo.y, left: geo.x,
      width: geo.w, height: geo.h,
      zIndex: geo.zIndex ?? 30,
      opacity, background: 'rgba(0,0,0,0.4)',
      borderRadius: 6, padding: 6,
      display: 'flex', alignItems: 'center', justifyContent: 'center',
      boxSizing: 'border-box',
    }}>
      <Img
        src={logoAsset.url}
        style={{ maxWidth: '100%', maxHeight: '100%', objectFit: 'contain', display: 'block' }}
      />
    </div>
  );
};

// ─── Story Counter Badge ───────────────────────────────────────────────────────

const StoryCounterBadge: React.FC<{
  text: string;
  bgColor: string;
  accentColor: string;
  geo: OverlayElementGeometry;
}> = ({ text, bgColor, accentColor, geo }) => {
  const frame = useCurrentFrame();
  const x = interpolate(frame, [0, 14], [-geo.w, 0], { extrapolateRight: 'clamp' });

  return (
    <div style={{
      position: 'absolute',
      top: geo.y, left: geo.x,
      width: geo.w, height: geo.h,
      zIndex: geo.zIndex ?? 10,
      background: bgColor, padding: '8px 20px 8px 16px',
      borderRight: `4px solid ${accentColor}`,
      transform: `translateX(${x}px)`,
      display: 'flex', alignItems: 'center', gap: 10,
      boxSizing: 'border-box',
    }}>
      <div style={{ width: 4, height: 18, background: accentColor, borderRadius: 2, flexShrink: 0 }} />
      <div style={{
        fontFamily: 'Roboto, sans-serif', fontSize: geo.fontSize,
        fontWeight: 800, color: '#fff', letterSpacing: 1.5,
        textTransform: 'uppercase', whiteSpace: 'nowrap',
      }}>
        {text}
      </div>
    </div>
  );
};

// ─── Breaking Banner ───────────────────────────────────────────────────────────

const BreakingBanner: React.FC<{
  label: string;
  bgColor: string;
  geo: OverlayElementGeometry;
  oc?: OverlayColorOverride;
}> = ({ label, bgColor, geo, oc }) => {
  const resolvedBg = oc?.bgColor ?? bgColor;
  const frame = useCurrentFrame();
  const pulse = Math.sin(frame * 0.12) * 0.25 + 0.75;
  const y = interpolate(frame, [0, 10], [-geo.h, 0], { extrapolateRight: 'clamp' });

  return (
    <div style={{
      position: 'absolute',
      top: geo.y, left: geo.x,
      width: geo.w, height: geo.h,
      zIndex: geo.zIndex ?? 50,
      background: resolvedBg,
      display: 'flex', alignItems: 'center', padding: '0 24px', gap: 14,
      transform: `translateY(${y}px)`,
      overflow: 'hidden',
      boxSizing: 'border-box',
    }}>
      {oc?.bgImageUrl && (
        <img src={oc.bgImageUrl} alt="" style={{ position: 'absolute', inset: 0, width: '100%', height: '100%', objectFit: 'cover' }} />
      )}
      <div style={{ position: 'relative', zIndex: 1, display: 'flex', alignItems: 'center', gap: 14 }}>
        <div style={{ width: 12, height: 12, borderRadius: '50%', background: '#fff', opacity: pulse, flexShrink: 0 }} />
        <div style={{
          background: '#fff', color: resolvedBg,
          fontFamily: 'Roboto, sans-serif', fontSize: geo.fontSize * 0.6,
          fontWeight: 900, letterSpacing: 2, padding: '2px 8px',
          borderRadius: 2, textTransform: 'uppercase', flexShrink: 0,
        }}>
          LIVE
        </div>
        <div style={{
          fontFamily: 'Roboto, sans-serif', fontSize: geo.fontSize,
          fontWeight: 900, color: oc?.textColor ?? '#fff', letterSpacing: 2,
          textTransform: 'uppercase',
        }}>
          {label}
        </div>
      </div>
    </div>
  );
};

// ─── Ticker ────────────────────────────────────────────────────────────────────

const Ticker: React.FC<{
  text: string;
  bgColor: string;
  textColor: string;
  accentColor: string;
  speed: number;
  geo: OverlayElementGeometry;
  oc?: OverlayColorOverride;
}> = ({ text, bgColor, textColor, accentColor, speed, geo, oc }) => {
  const frame = useCurrentFrame();
  const { width } = useVideoConfig();

  const repeated = `${text}  ●  ${text}  ●  ${text}`;
  const charWidth = geo.fontSize * 0.65;
  const totalWidth = repeated.length * charWidth;
  const offset = (frame * speed) % totalWidth;

  const resolvedBg     = oc?.bgColor    ?? bgColor;
  const resolvedText   = oc?.textColor  ?? textColor;
  const resolvedAccent = oc?.accentColor ?? accentColor;

  return (
    <div style={{
      position: 'absolute',
      top: geo.y, left: geo.x,
      width: geo.w, height: geo.h,
      zIndex: geo.zIndex ?? 40,
      background: resolvedBg,
      display: 'flex', alignItems: 'stretch',
      overflow: 'hidden',
      boxSizing: 'border-box',
    }}>
      {oc?.bgImageUrl && (
        <img src={oc.bgImageUrl} alt="" style={{ position: 'absolute', inset: 0, width: '100%', height: '100%', objectFit: 'cover' }} />
      )}
      {/* Left accent tab */}
      <div style={{
        position: 'relative', zIndex: 1,
        width: Math.max(60, geo.fontSize * 4), background: resolvedAccent, flexShrink: 0,
        display: 'flex', alignItems: 'center', justifyContent: 'center',
      }}>
        <div style={{
          fontFamily: 'Roboto, sans-serif', fontSize: geo.fontSize * 0.55,
          fontWeight: 900, color: '#000', letterSpacing: 2,
          textTransform: 'uppercase',
        }}>
          NEWS
        </div>
      </div>

      {/* Scrolling text */}
      <div style={{ position: 'relative', zIndex: 1, flex: 1, overflow: 'hidden', display: 'flex', alignItems: 'center' }}>
        <div style={{
          transform: `translateX(${width - offset}px)`,
          whiteSpace: 'nowrap',
          fontFamily: 'Roboto, sans-serif', fontSize: geo.fontSize,
          fontWeight: 600, color: resolvedText, letterSpacing: 0.3,
        }}>
          {repeated}
        </div>
      </div>
    </div>
  );
};

// ─── Main Export ───────────────────────────────────────────────────────────────

export interface GlobalOverlayProps {
  props: SceneProps;
}

export const GlobalOverlay: React.FC<GlobalOverlayProps> = ({ props }) => {
  const {
    colorPalette,
    showTicker, tickerText, tickerSpeed,
    showLogo, assets,
    showTopBar, topBarText,
    showStoryCounter, storyCounterText,
    isBreaking, breakingLabel,
    overlayGeometry,
    overlayColors,
  } = props;

  // Merge per-layout geometry with defaults
  const geo = {
    ticker:         { ...DEFAULT_OVERLAY_GEOMETRY.ticker,         ...(overlayGeometry?.ticker) },
    topBar:         { ...DEFAULT_OVERLAY_GEOMETRY.topBar,         ...(overlayGeometry?.topBar) },
    logo:           { ...DEFAULT_OVERLAY_GEOMETRY.logo,           ...(overlayGeometry?.logo) },
    storyCounter:   { ...DEFAULT_OVERLAY_GEOMETRY.storyCounter,   ...(overlayGeometry?.storyCounter) },
    breakingBanner: { ...DEFAULT_OVERLAY_GEOMETRY.breakingBanner, ...(overlayGeometry?.breakingBanner) },
  };

  const topBarActive = !isBreaking && !!showTopBar;
  const breakingActive = !!isBreaking && !!breakingLabel;

  return (
    <AbsoluteFill style={{ pointerEvents: 'none' }}>
      {breakingActive && (
        <BreakingBanner label={breakingLabel!} bgColor={colorPalette.breakingBanner} geo={geo.breakingBanner} oc={overlayColors?.breakingBanner} />
      )}

      {topBarActive && (
        <TopBar
          text={topBarText}
          bgColor={colorPalette.primary}
          accentColor={colorPalette.accent}
          geo={geo.topBar}
          oc={overlayColors?.topBar}
        />
      )}

      {showLogo && (
        <LogoBadge
          logoAsset={assets.logo}
          accentColor={colorPalette.accent}
          geo={geo.logo}
        />
      )}

      {showStoryCounter && storyCounterText && (
        <StoryCounterBadge
          text={storyCounterText}
          bgColor={colorPalette.secondary}
          accentColor={colorPalette.accent}
          geo={geo.storyCounter}
        />
      )}

      {showTicker && tickerText && (
        <Ticker
          text={tickerText}
          bgColor={colorPalette.ticker}
          textColor={colorPalette.tickerText}
          accentColor={colorPalette.accent}
          speed={tickerSpeed ?? 3}
          geo={geo.ticker}
          oc={overlayColors?.ticker}
        />
      )}
    </AbsoluteFill>
  );
};
